import CentricDispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes } from '../constants/CentricConstants';
import EventEmitter from 'events';
import assign from 'object-assign';
import UserStore from './UserStore';
import { asDate } from '../utils/Date';

const CHANGE_EVENT = 'change';
const FORM_CHANGE_EVENT = 'form_change';

let _entities = [];
let _statusOptions= [];
let _fetchedAll = false;

const _statuses = [
    { name: null,        id: null,        next: ['waiting', 'enrolled']    },
    { name: 'Waiting',   id: 'waiting',   next: ['waiting', 'enrolled', 'cancelled']  },
    { name: 'Enrolled',  id: 'enrolled',  next: ['enrolled', 'cancelled', 'completed'] },
    { name: 'Cancelled', id: 'cancelled', next: ['waiting', 'enrolled', 'cancelled'] },
    { name: 'Completed', id: 'completed', next: ['completed', 'enrolled'] },
];

const sanitizeEntity = ({id, categoryId, accessLeft, accessRight, ...entity}) => ({
    ...entity,
    id: Number(id),
    mediaCode: entity.uuid,
    categoryId: Number(categoryId) || null,
    vendorId: Number(entity.vendorId) || null,
    eventId: Number(entity.eventId) || null,
    validFrom: asDate(entity.validFrom),
    validTo: asDate(entity.validTo),
    launchAsPopup: (entity.type === 'e-learning' ? !!entity.launchAsPopup : null),
    eventTemplateId: Number(entity.eventTemplateId) || null,
    duration: (!!entity.duration || entity.duration === 0) ? Number(entity.duration) : null,
    access: entity.accessGroupId && {
        id: Number(entity.accessGroupId),
        name: entity.accessGroup || null,
        left: accessLeft,
        right: accessRight
    }
});

const getFilter = ({skillId = null}) => e => (!!e && (e.skillId === skillId));
const getRemoveFilter = ({skillId = null}) => e => (!!e && (e.skillId !== skillId));

let ModuleStore = assign({}, EventEmitter.prototype, {

    emitChange: function() {
        this.emit(CHANGE_EVENT);
    },
    emitChangeForm: function() {
        this.emit(FORM_CHANGE_EVENT);
    },
    addChangeListener: function(callback) {
        this.on(CHANGE_EVENT, callback);
    },
    addFormChangeListener: function(callback) {
        this.on(FORM_CHANGE_EVENT, callback);
    },

    addListener: function(callback) {
        this.addChangeListener(callback);
        return {remove: () => this.removeChangeListener(callback) };
    },

    removeChangeListener: function(callback) {
        this.removeListener(CHANGE_EVENT, callback);
    },
    getStatusOptions: function() {
        return _statusOptions;
    },
    getById: function(id) {
        id = Number(id);
        return _entities.find( (e) => (e !== undefined && e.id === id) );
    },
    getByIds: (moduleIds) => _entities.filter(({id}) => moduleIds.includes(id)),
    getByCategory: function(categoryId) {
        categoryId = Number(categoryId);
        if (!categoryId) { return []; }
        return _entities.filter((e) => (e !== undefined && e.categoryId === categoryId) );
    },
    getByType: (type) => _entities.filter(e => (e !== undefined && e.type === type)),
    getAll: () => _entities.slice(),
    getBy: (filter) => _entities.filter(getFilter(filter)),
    getMy: (type) => _entities.filter((e) => (e !== undefined && e.enrolmentStatus !== undefined && (!type || e.type === type))),
    getMyResources: () => _entities.filter((e) => e !== undefined && e.enrolmentStatus !== undefined && e.type === 'resource'),
    getByEvent: (eventId) => _entities.find((e) => e !== undefined && e.eventId === eventId),

    add: function(entity) {
        entity = sanitizeEntity(entity);
        const entityIndex = _entities.findIndex( (e) => (e !== undefined && e.id === entity.id) );

        if (entityIndex < 0) {
            _entities.push(entity);
        } else {
            _entities[entityIndex] = entity;
        }
    },

    delete: function(id) {
        id = Number(id);
        let entityIndex = _entities.findIndex( (e) => (e !== undefined && e.id === id) );
        if (entityIndex >= 0) {
            delete _entities[entityIndex];
        }
    },

    removeUserWaitingList: function(id, waitingListId) {
        let module = ModuleStore.getById(id);
        waitingListId = Number(waitingListId);
        if (module.waitingLists !== undefined && module.waitingLists.length > 0) {
            let index = module.waitingLists.findIndex(function(e) {
                return e !== undefined && e.id === waitingListId;
            });

            if(index >= 0) {
                delete module.waitingLists[index];
                ModuleStore.add(module);
            }
        }
    },
    addUserWaitingList: function(id, userId) {
        let module = ModuleStore.getById(id);
        let index = -1;
        userId = Number(userId);

        if (module.waitingLists !== undefined && module.waitingLists.length > 0) {
            index = module.waitingLists.findIndex(function(e) {
                return e !== undefined && e.id === userId;
            });
        }
        if (index < 0) {
            let user = UserStore.getById(userId);
            module.waitingLists.push(user);
            ModuleStore.add(module);
        }
    },

    getStatuses: function() {
        return _statuses.filter(status => (status !== undefined && status.id !== null))
            .map(status => ({id: status.id, name: status.name }));
    },

    fetchedAll: () => (_fetchedAll)

});

ModuleStore.dispatchToken = CentricDispatcher.register(function(action) {

    switch(action.type) {
        case ActionTypes.MODULE_LIST:
            if (!!action.payload) {
                if (!!action.payload.clearAll) {
                    _entities = []; // should be only if needed
                } else if (action.payload.hasOwnProperty('skillId')){
                    _entities = _entities.filter(getRemoveFilter(action.payload));
                }
            }
            action.data.forEach(ModuleStore.add);
            _fetchedAll = action.data.length === 0;
            ModuleStore.emitChange();
            break;

        case ActionTypes.MODULE_LIST_ERROR:
            ModuleStore.emitChange();
            break;

        case ActionTypes.MODULE_INFO:
            ModuleStore.add(action.data);
            ModuleStore.emitChange();
            break;

        case ActionTypes.MODULE_INFO_ERROR:
            ModuleStore.emitChange();
            break;

        case ActionTypes.MODULE_UPDATE:
            ModuleStore.add(action.data);
            ModuleStore.emitChange();
            break;

        case ActionTypes.MODULE_DELETE:
            if (!!action.data.deleted && !!action.data.id) {
                ModuleStore.delete(action.data.id);
            }
            ModuleStore.emitChange();
            break;

        case ActionTypes.MODULE_STATUS_OPTION_LIST:
            _statusOptions = action.data;
            ModuleStore.emitChange();
            break;

        case ActionTypes.MODULE_MEDIA_PUBLISH_SUCCESS:
            if (!!action.data.moduleId) {
                const moduleId = Number(action.data.moduleId);
                const index = _entities.findIndex(e => !!e && e.id === moduleId);
                if (-1 < index && _entities[index].mediaCode !== action.data.id) {
                    _entities = _entities.map((e, i) => i !== index ? e : ({
                        ..._entities[index],
                        uuid: action.data.id,
                        mediaCode: action.data.id
                    }));
                }
                ModuleStore.emitChange();
            }
            break;

        default:
            break;
    }

});

export default ModuleStore;

export const getNextStatuses = (status)  => {
    if (status === undefined) { status = null; }
    const s = _statuses.find((e) => (e !== undefined && e.id === status ));
    if (s === undefined) { return []; } // guard

    return s.next.map((e) => {
        const i = _statuses.find((f) => (f !== undefined && f.id === e) );
        return {id: i.id, name: i.name};
    });
};
